/**
 * Validation Schemas
 * Zod schemas for validating API request data
 */

import { z } from 'zod';
import { isValid, parseISO } from 'date-fns';
import { RSVP_SIZES } from '../../src/types/units';

/** YYYY-MM-DD that parses to a real date */
export const isoDateSchema = z.string().refine((value) => isValid(parseISO(value)), {
  message: 'weekStartDate must be a valid ISO date (YYYY-MM-DD)',
});

export const passwordSchema = z.string()
  .min(8, 'Password must be at least 8 characters')
  .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
  .regex(/[0-9]/, 'Password must contain at least one number');

/**
 * Registration schema
 * Validates member registration data
 */
export const registrationSchema = z.object({
  itsId: z.string()
    .min(1, 'ITS ID is required')
    .regex(/^\d+$/, 'ITS ID must contain only digits'),

  name: z.string()
    .min(1, 'Name is required')
    .max(255, 'Name must be less than 255 characters'),

  phone: z.string()
    .min(1, 'Phone number is required')
    .max(20, 'Phone number must be less than 20 characters'),

  email: z.string()
    .email('Invalid email address')
    .max(255, 'Email must be less than 255 characters')
    .optional()
    .or(z.literal('')), // Allow empty string for optional email

  password: passwordSchema,

  confirmPassword: z.string()
    .min(1, 'Please confirm your password'),

  pickupLocationId: z.coerce.number().int().positive('Pickup location is required'),

  defaultSize: z.enum(RSVP_SIZES)
    .optional()
    .nullable(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ['confirmPassword'],
});

export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1, 'Current password is required'),
  newPassword: passwordSchema,
  confirmPassword: z.string().min(1, 'Please confirm your new password'),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: "Passwords don't match",
  path: ['confirmPassword'],
}).refine((data) => data.currentPassword !== data.newPassword, {
  message: 'New password must be different from your current password',
  path: ['newPassword'],
});

/**
 * Admin-assisted password reset schema
 * Used by POST /api/admin/members/:id/reset-password
 */
export const adminResetPasswordSchema = z.object({
  newPassword: passwordSchema,
}).strict();

export type RegistrationData = z.infer<typeof registrationSchema>;
export type ChangePasswordData = z.infer<typeof changePasswordSchema>;
export type AdminResetPasswordData = z.infer<typeof adminResetPasswordSchema>;
